import React, { useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { COUNTRIES, DESTINATIONS } from "@/data/destinations";
import { useLang } from "@/LanguageContext";
import { trackPageview } from "@/lib/track";

export default function CountryDestinations() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const isFr = lang === "fr";
  const country = COUNTRIES.find((c) => c.slug === slug);
  const cities = DESTINATIONS.filter((d) => d.country === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageview();
  }, [slug]);

  useEffect(() => {
    if (country) {
      document.title = `${isFr ? country.fr : country.en} — French Touch Concierge`;
    }
  }, [country, isFr]);

  if (!country) return <Navigate to="/" replace />;

  const goContact = () => navigate("/#contact");

  return (
    <div data-testid="country-page" className="App bg-ivory min-h-screen">
      <Navbar />

      <header className="bg-navy pt-32 pb-16 md:pt-40 md:pb-20 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <button
              data-testid="back-to-countries"
              onClick={() => navigate("/#destinations")}
              className="text-gold text-xs tracking-luxe uppercase hover:text-gold-light transition-colors"
            >
              {isFr ? "← Tous les pays" : "← All countries"}
            </button>
            <span className="tricolore mt-8" style={{ width: "160px", height: "3px" }}>
              <span className="bg-frblue" /><span className="bg-white" /><span className="bg-frred" />
            </span>
            <h1 data-testid="country-title" className="font-serif-lux text-white text-4xl sm:text-5xl lg:text-6xl font-light mt-6">
              {isFr ? country.fr : country.en}
            </h1>
            <p className="text-white/60 font-light max-w-xl mt-4">
              {isFr
                ? `${cities.length} destination${cities.length > 1 ? "s" : ""} d'exception — chacune avec son expérience signature.`
                : `${cities.length} signature destination${cities.length > 1 ? "s" : ""} — each with its own unrepeatable experience.`}
            </p>
          </motion.div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div data-testid="country-destinations-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
          {cities.map((d, i) => (
            <motion.button
              key={d.slug}
              onClick={goContact}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
              data-testid={`destination-${d.slug}`}
              className="group relative overflow-hidden h-[340px] cursor-pointer text-left"
            >
              <img src={d.img} alt={isFr ? d.nameFr : d.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
              <div className="absolute inset-0 border border-transparent group-hover:border-gold/60 m-3 transition-all duration-500 pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <p className="text-gold-light text-[10px] tracking-luxe uppercase">{isFr ? country.fr : country.en}</p>
                <h3 className="font-serif-lux text-white text-2xl md:text-3xl leading-tight mt-1 group-hover:text-gold-light transition-colors duration-500">
                  {isFr ? d.nameFr : d.name}
                </h3>
                <div className="gold-line w-8 my-3 opacity-60" />
                <p className="font-serif-lux italic text-white/80 text-sm leading-snug">{isFr ? d.fr : d.en}</p>
              </div>
            </motion.button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-20 text-center border border-gold/30 bg-white/40 px-6 py-12 md:py-16"
        >
          <p className="text-gold text-xs tracking-luxe uppercase">{isFr ? "Sur Mesure" : "Bespoke"}</p>
          <h2 className="font-serif-lux text-navy text-3xl md:text-4xl font-light mt-4 max-w-2xl mx-auto">
            {isFr
              ? `Votre voyage en ${country.fr} commence par une conversation.`
              : `Your journey to ${country.en} begins with a conversation.`}
          </h2>
          <button
            data-testid="country-cta"
            onClick={goContact}
            className="btn-lux mt-8 border border-navy text-navy hover:bg-navy hover:text-gold px-8 py-3 text-xs tracking-luxe uppercase transition-colors"
          >
            {isFr ? "Parler à un Concierge Privé" : "Speak With a Private Concierge"}
          </button>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
